import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Logo from '@/components/Logo';
import { Loader2 } from 'lucide-react';
import { callClaimAgent, callGetAgentForClaim, signInWithTwitter } from '@/lib/firebase';
import type { AgentProfile } from '@molt-starter/shared';

export function ClaimPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [claiming, setClaiming] = useState(false);
  const [loading, setLoading] = useState(true);
  const [agentProfile, setAgentProfile] = useState<AgentProfile | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch agent profile on mount
  useEffect(() => {
    if (!token) {
      navigate('/claim-error?error=invalid_token');
      return;
    }

    const fetchAgentProfile = async () => {
      try {
        setLoading(true);
        const result = await callGetAgentForClaim({ claimToken: token });
        setAgentProfile(result.data);
      } catch (err: any) {
        console.error('Failed to fetch agent profile:', err);
        
        // Navigate to error page based on error code
        if (err.code?.includes('not-found')) {
          navigate('/claim-error?error=invalid_token');
        } else if (err.code?.includes('failed-precondition')) {
          navigate('/claim-error?error=already_claimed');
        } else {
          navigate('/claim-error?error=unknown');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAgentProfile();
  }, [token, navigate]);

  const handleClaimWithX = async () => {
    if (!token) return;

    try {
      setClaiming(true);
      setError(null);

      // Step 1: Sign in with Twitter using Firebase Auth
      await signInWithTwitter();

      // Step 2: Call the claimAgent function
      const result = await callClaimAgent({ claimToken: token });

      // Step 3: Navigate to success page with verification code
      navigate(
        `/claim-success?agent=${result.data.id}` +
        `&name=${encodeURIComponent(result.data.name)}` +
        `&code=${encodeURIComponent(result.data.verificationCode)}`
      );
    } catch (err: any) {
      console.error('Failed to claim agent:', err);
      
      // Handle specific errors
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign-in was cancelled. Please try again.');
      } else if (err.code === 'auth/account-exists-with-different-credential') {
        setError('This X account is already linked to another user.');
      } else if (err.code?.includes('failed-precondition')) {
        // Check the message to distinguish between "already claimed" and "user has agent"
        if (err.message?.includes('already own an agent')) {
          navigate('/claim-error?error=user_has_agent');
        } else {
          navigate('/claim-error?error=already_claimed');
        }
      } else if (err.code?.includes('not-found')) {
        navigate('/claim-error?error=invalid_token');
      } else {
        setError(err.message || 'Failed to claim agent. Please try again.');
      }
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto">
            <Logo size={64} />
          </div>
          <div className="space-y-2">
            <CardTitle className="text-2xl font-bold">
              {loading ? 'Loading...' : agentProfile ? `Claim ${agentProfile.name}` : 'Claim Your Agent'}
            </CardTitle>
            <CardDescription className="text-base">
              {loading ? 'Fetching agent details...' : 'Sign in with X (Twitter) to verify ownership'}
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-8 space-y-3">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Verifying claim token...</p>
            </div>
          )}

          {/* Agent Profile Loaded */}
          {!loading && agentProfile && (
            <>
              {/* Error Message */}
              {error && (
                <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-3">
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              {/* Verification Code Display */}
              <div className="rounded-lg border-2 border-primary/30 bg-primary/5 p-6 space-y-3">
                <div className="flex items-center gap-2 text-primary">
                  <span className="material-symbols-outlined text-2xl">
                    verified_user
                  </span>
                  <h3 className="font-semibold text-lg">Verification Code</h3>
                </div>
                <div className="bg-background rounded-md p-4 border border-border">
                  <p className="text-3xl font-mono font-bold text-center tracking-wider text-foreground">
                    {agentProfile.verificationCode}
                  </p>
                </div>
                <p className="text-sm text-muted-foreground">
                  <strong>Important:</strong> Make sure this code matches what your agent displayed. 
                  This verifies you're claiming the correct agent.
                </p>
              </div>

              {/* Agent Details */}
              <div className="rounded-lg border border-border bg-card p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-muted-foreground">
                    robot_2
                  </span>
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground">Agent Name</p>
                    <p className="font-semibold text-foreground">{agentProfile.name}</p>
                  </div>
                </div>
              </div>

              {/* Info */}
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="material-symbols-outlined text-primary text-xl mt-0.5">
                    info
                  </span>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-foreground">
                      Why verify with X?
                    </p>
                    <p className="text-xs text-muted-foreground">
                      We use X authentication to ensure you're the legitimate owner of this agent. 
                      Each X account can claim only one agent.
                    </p>
                  </div>
                </div>
              </div>

              {/* Claim Button */}
              <Button
                onClick={handleClaimWithX}
                className="w-full h-12 text-base font-medium"
                size="lg"
                disabled={claiming}
              >
                {claiming ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Claiming...
                  </span>
                ) : (
                  <span className="flex items-center gap-3">
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    Sign in with X to Claim
                  </span>
                )}
              </Button>

              {/* Footer */}
              <div className="text-center">
                <p className="text-xs text-muted-foreground">
                  Already claimed?{' '}
                  <button
                    onClick={() => navigate('/login')}
                    className="text-primary hover:underline"
                  >
                    Sign in here
                  </button>
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
